import * as React from 'react';
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Grid2
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTheme } from '@emotion/react';
import SwtichButton from './SwitchButton';
import dayjs from 'dayjs';
import ButtonNormal from './ButtonNormal'
import '../services/GlobalVariables'

export default function StickyHeadTable(props) {
    const theme = useTheme();

    const perPage = props.perPage;
    const tableColumns = props.tableColumns;
    const rows = props.tableData;

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(perPage || 5);
    const [filter, setFilter] = React.useState('');
    const [filteredRows, setFilteredRows] = React.useState(rows);

    // Sorting states
    const [orderBy, setOrderBy] = React.useState('');
    const [order, setOrder] = React.useState('asc');

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleFilterChange = (event) => {
        const filterValue = event.target.value.toLowerCase();
        setFilter(filterValue);

        const filteredData = rows.filter(row => {
            return tableColumns.some(col => {
                const cellValue = row[col.fieldName]?.toString().toLowerCase();
                return cellValue && cellValue.includes(filterValue);
            });
        });

        setFilteredRows(filteredData);
    };

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(column);
    };

    const sortedRows = React.useMemo(() => {
        if (!orderBy) return filteredRows;
        return [...filteredRows].sort((a, b) => {
            const aVal = a[orderBy];
            const bVal = b[orderBy];

            if (aVal === undefined || bVal === undefined) return 0;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return order === 'asc' ? aVal - bVal : bVal - aVal;
            } else {
                return order === 'asc'
                    ? aVal.toString().localeCompare(bVal.toString())
                    : bVal.toString().localeCompare(aVal.toString());
            }
        });
    }, [filteredRows, orderBy, order]);

    React.useEffect(() => {
        setFilteredRows(rows);
    }, [rows]);

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden', backgroundColor: 'rgb(250, 250, 250)' }}>
            <Grid2 container spacing={1} justifyContent="center" alignItems="center">
                <Grid2 size={4}>
                    <TextField
                        sx={{ margin: '20px', width: '100%' }}
                        label="Filter"
                        variant="outlined"
                        fullWidth
                        value={filter}
                        onChange={handleFilterChange}
                        placeholder="Search by any column..."
                        size="small"
                    />
                </Grid2>
            </Grid2>

            <TableContainer>
                <Table stickyHeader aria-label="sticky table" size="small">
                    <TableHead>
                        <TableRow>
                            {tableColumns.slice(1, -3).map((col, index) => (
                                col.status === 'en' && (
                                    <TableCell
                                        key={index}
                                        style={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            textAlign: col.textAlign,
                                            fontWeight: 'bolder',
                                            width: col.width,
                                            textShadow: 'black 2px 2px 4px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleSort(col.fieldName)}
                                        sortDirection={orderBy === col.fieldName ? order : false}
                                    >
                                        {/* <TableSortLabel
                                            active={orderBy === col.fieldName}
                                            direction={orderBy === col.fieldName ? order : 'asc'}
                                            onClick={() => handleSort(col.fieldName)}
                                            style={{ color: 'white' }}
                                        >
                                            {col.cName}
                                        </TableSortLabel> */}
                                        {col.cName}
                                    </TableCell>
                                )
                            ))}
                            {tableColumns.slice(-3).map((col, i) =>
                                col.status === 'en' ? (
                                    <TableCell
                                        key={`action-${i}`}
                                        style={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: 'white',
                                            textAlign: col.textAlign,
                                            fontWeight: 'bolder',
                                            textShadow: 'black 2px 2px 4px',
                                            cursor: 'pointer',
                                            width: col.width
                                        }}
                                    >
                                        {col.cName}
                                    </TableCell>
                                ) : null
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {tableColumns.slice(1, -3).map((col, index) => (
                                        <TableCell
                                            key={index}
                                            style={{
                                                textAlign: col.textAlign,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                border: 'ridge 1px'
                                            }}
                                        >
                                            {col.dType === 'currency'
                                                ? `$${Math.round(row[col.fieldName]).toLocaleString("en-US")}`
                                                : col.dType === 'number'
                                                    ? `${Math.round(row[col.fieldName]).toLocaleString("en-US")}`
                                                    : col.swithButton ? (
                                                        <SwtichButton
                                                            status={row[col.fieldName]}
                                                            funcOnChange={props.onChangeSwitch}
                                                        />
                                                    ) : col.dType === 'date' ? (
                                                        dayjs(row[col.fieldName]).format('DD-MM-YYYY')
                                                    ) : col.dType === 'decimal' ? (
                                                        `$${(Math.round(row[col.fieldName] * 1000) / 1000).toLocaleString("en-US")}`
                                                    ) : tableColumns[index + 1].dType === 'img'
                                                        ? <>
                                                            <img
                                                                src={row[tableColumns[index + 1].fieldName] !== null ? `${global.SET_API_URL}/employeesImages/${row[tableColumns[index + 1].fieldName]}` : `${global.SET_API_URL}/images/1759242661144-maltexLogo.png`}
                                                                alt={row[tableColumns[index + 1].fieldName]}
                                                                style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                                                            />
                                                        </> : (
                                                            row[col.fieldName]
                                                        )}
                                        </TableCell>
                                    ))}
                                    {/* Action buttons */}
                                    {tableColumns[tableColumns.length - 3].status === 'en' && (
                                        <TableCell style={{ textAlign: 'center', border: 'ridge 1px' }}>
                                            <EditIcon
                                                sx={{
                                                    cursor: props.canEditRow && !props.canEditRow(row) ? 'not-allowed' : 'pointer',
                                                    opacity: props.canEditRow && !props.canEditRow(row) ? 0.3 : 1,
                                                    // color: theme.palette.primary.main,
                                                    // border: `1px solid ${theme.palette.primary.main}`,
                                                    // borderRadius: 1,
                                                    // p: 0.5,
                                                    // boxShadow: '1px 1px 1px 0px grey'
                                                }}
                                                onClick={() => {
                                                    if (props.canEditRow && !props.canEditRow(row)) return;
                                                    const onEditClick = props.onEditClick;
                                                    onEditClick(row[tableColumns[0].fieldName]);
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    {tableColumns[tableColumns.length - 2].status === 'en' && (
                                        <TableCell style={{ textAlign: 'center', border: 'ridge 1px' }}>
                                            <DeleteForeverIcon
                                                sx={{
                                                    cursor: props.canDeleteRow && !props.canDeleteRow(row) ? 'not-allowed' : 'pointer',
                                                    opacity: props.canDeleteRow && !props.canDeleteRow(row) ? 0.3 : 1,
                                                    // color: theme.palette.primary.main,
                                                    // border: `1px solid ${theme.palette.primary.main}`,
                                                    // borderRadius: 1,
                                                    // p: 0.5,
                                                    // boxShadow: '1px 1px 1px 0px grey'
                                                }}
                                                onClick={() => {
                                                    if (props.canDeleteRow && !props.canDeleteRow(row)) return;
                                                    const onDeleteClick = props.onDeleteClick;
                                                    onDeleteClick(row[tableColumns[0].fieldName]);
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    {tableColumns[tableColumns.length - 1].status === 'en' && (
                                        <TableCell style={{ textAlign: 'center', border: 'ridge 1px' }}>
                                            {props.actionIcon === 'view' ? (
                                                <VisibilityIcon
                                                    titleAccess='View details'
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => props.onButtonClick(row[tableColumns[0].fieldName])}
                                                />
                                            ) : (
                                                <ButtonNormal
                                                    onClick={() => {
                                                        const onButtonClick = props.onButtonClick;
                                                        onButtonClick(row[tableColumns[0].fieldName]);
                                                    }}
                                                    text={tableColumns[tableColumns.length - 1].buttonText}
                                                />
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={sortedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}
